import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, seedAPI } from '../../lib/api';
import { formatDate, canAccessNav } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Newspaper, Image, GraduationCap, Calendar, FileText, 
  Mail, TrendingUp, ArrowRight, Plus
} from 'lucide-react';
import { Button } from '../ui/button';

const statCards = [
  { key: 'news_count', label: 'Aktuality', icon: Newspaper, color: '#4ECDC4', link: '/admin/aktuality', navKey: 'news' },
  { key: 'albums_count', label: 'Galerie', icon: Image, color: '#FFB703', link: '/admin/galerie', navKey: 'gallery' },
  { key: 'classes_count', label: 'Třídy', icon: GraduationCap, color: '#95E1D3', link: '/admin/tridy', navKey: 'classes' },
  { key: 'events_count', label: 'Události', icon: Calendar, color: '#FF6B6B', link: '/admin/udalosti', navKey: 'events' },
  { key: 'documents_count', label: 'Dokumenty', icon: FileText, color: '#7C4DFF', link: '/admin/dokumenty', navKey: 'documents' },
  { key: 'unread_messages', label: 'Nepřečtené zprávy', icon: Mail, color: '#26A69A', link: '/admin/zpravy', navKey: 'messages' }
];

const quickActions = [
  { to: '/admin/aktuality/nova', label: 'Přidat aktualitu', icon: Newspaper, navKey: 'news' },
  { to: '/admin/galerie/nova', label: 'Nové album', icon: Image, navKey: 'gallery' },
  { to: '/admin/udalosti/nova', label: 'Přidat událost', icon: Calendar, navKey: 'events' },
  { to: '/admin/jidelnicek', label: 'Upravit jídelníček', icon: TrendingUp, navKey: 'menu' }
];

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const role = user?.role;
  const allowedStats = statCards.filter((c) => canAccessNav(role, c.navKey));
  const allowedActions = quickActions.filter((a) => canAccessNav(role, a.navKey));
  const canNews = canAccessNav(role, 'news');
  const canEvents = canAccessNav(role, 'events');

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Try to seed data first
        await seedAPI.seedData().catch(() => {});
        
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Dashboard</h1>
          <p className="text-[#64748B]">Přehled obsahu a statistiky</p>
        </div>
        <div className="flex gap-3">
          {canNews && (
            <Link to="/admin/aktuality/nova">
              <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-news-btn">
                <Plus size={18} className="mr-2" />
                Nová aktualita
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {allowedStats.map((card) => {
          const Icon = card.icon;
          const value = stats?.[card.key] || 0;
          return (
            <Link 
              key={card.key} 
              to={card.link}
              className="stat-card hover:shadow-lg transition-shadow"
              data-testid={`stat-${card.key}`}
            >
              <div 
                className="stat-card-icon mb-3"
                style={{ backgroundColor: `${card.color}20` }}
              >
                <Icon size={24} style={{ color: card.color }} />
              </div>
              <div className="text-2xl font-bold text-[#2C3E50]">{value}</div>
              <div className="text-sm text-[#64748B]">{card.label}</div>
            </Link>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent News */}
        {canNews && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[#2C3E50]">Poslední aktuality</h2>
            <Link to="/admin/aktuality" className="text-sm text-[#4ECDC4] hover:underline flex items-center gap-1">
              Vše <ArrowRight size={14} />
            </Link>
          </div>
          
          {stats?.recent_news?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_news.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/admin/aktuality/${item.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#4ECDC4]/10 flex items-center justify-center flex-shrink-0">
                    <Newspaper size={18} className="text-[#4ECDC4]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2C3E50] truncate">{item.title}</div>
                    <div className="text-xs text-[#64748B]">{formatDate(item.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              Žádné aktuality
            </div>
          )}
        </div>
        )}

        {/* Upcoming Events */}
        {canEvents && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[#2C3E50]">Nadcházející akce</h2>
            <Link to="/admin/udalosti" className="text-sm text-[#4ECDC4] hover:underline flex items-center gap-1">
              Vše <ArrowRight size={14} />
            </Link>
          </div>
          
          {stats?.upcoming_events_list?.length > 0 ? (
            <div className="space-y-4">
              {stats.upcoming_events_list.map((event) => (
                <Link 
                  key={event.id} 
                  to={`/admin/udalosti/${event.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#FF6B6B]/10 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-[#FF6B6B]">
                      {new Date(event.start_date).getDate()}
                    </span>
                    <span className="text-xs text-[#FF6B6B]">
                      {new Date(event.start_date).toLocaleDateString('cs-CZ', { month: 'short' })}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2C3E50] truncate">{event.title}</div>
                    <div className="text-xs text-[#64748B]">{formatDate(event.start_date)}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              Žádné nadcházející akce
            </div>
          )}
        </div>
        )}
      </div>

      {/* Quick Actions */}
      {allowedActions.length > 0 && (
      <div className="bg-gradient-to-r from-[#4ECDC4] to-[#95E1D3] rounded-2xl p-6 lg:p-8">
        <h2 className="text-xl font-bold text-white mb-4">Rychlé akce</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {allowedActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.to} to={a.to} className="bg-white/20 backdrop-blur-sm rounded-xl p-4 hover:bg-white/30 transition-colors">
                <Icon className="text-white mb-2" size={24} />
                <div className="text-white font-medium">{a.label}</div>
              </Link>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
};

export default DashboardPage;
